import { Injectable, inject } from '@angular/core';
import { GetFollowUpsListAction } from '@app/modules/follow-ups/store/follow-ups.actions';
import { FollowUpsState } from '@app/modules/follow-ups/store/follow-ups.state';
import { ServiceDataFormComponent } from '@app/modules/service-data/components/service-data/service-data-form/service-data-form.component';
import { getDateOnlyMoment } from '@app/modules/service-data/service-data.utils';
import { ServiceDataService } from '@app/modules/service-data/services/service-data.service';
import { GetSamplesListAction } from '@app/modules/service-data/store/sample.actions';
import { SampleState } from '@app/modules/service-data/store/sample.state';
import { SetServiceDataAction } from '@app/modules/service-data/store/service-data.actions';
import {
  ParentingTestType,
  PaternityParticipant,
  PaternityServiceInformation,
  SameBiologicalMotherAnswer,
} from '@app/modules/service-data/types/paternity.types';
import {
  Ethnicity,
  ExportDialogDataType,
  ExportType,
  ExtendedServiceData,
  ProvisionServiceData,
  ServiceData,
  ServiceDataExtensions,
  ServiceInformation,
} from '@app/modules/service-data/types/service-data.types';
import { DataInputType, PaymentType, RuntimeStatus } from '@core/core.types';
import { ExportersUtilsService } from '@core/services/exporters-utils.service';
import {
  LoadPartnerById,
  LoadPartnersByIds,
} from '@core/store/partners/partners.actions';
import { PartnersState } from '@core/store/partners/partners.state';
import {
  LoadInvoiceById,
  LoadInvoicesByIdsAction,
  LoadInvoicesPaymentsListAction,
} from '@core/store/payments/invoice-payments.actions';
import { InvoicesState } from '@core/store/payments/invoice-payments.state';
import { ProductsListState } from '@core/store/products/products.state';
import { Util, getSafeUTCDate } from '@core/utils/core.util';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { get } from 'lodash';
import moment from 'moment/moment';
import { Observable, combineLatest, forkJoin } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { map, switchMap, tap } from 'rxjs/operators';
import { ServiceDataDetailsComponent } from '../components/service-data/service-data-details/service-data-details.component';
import { ServiceDataResultsComponent } from '../components/service-data/service-data-results/service-data-results.component';
import { SampleServiceInformation } from '../types/sample.types';

@Injectable({
  providedIn: 'root',
})
export class ServiceDataUtilsService {
  private store = inject(Store);
  private ngbModal = inject(NgbModal);
  private exportersUtilsService = inject(ExportersUtilsService);
  private translate = inject(TranslateService);
  private serviceDataService = inject(ServiceDataService);
  private toastService = inject(ToastService);

  getExtendedServiceData(
    serviceData: ServiceData,
    appends: ServiceDataExtensions
  ): Observable<ExtendedServiceData> {
    if (!serviceData) {
      return of(null);
    }
    const doctorId = this.getPropertyFromServiceInformation(
      serviceData.serviceInformation,
      'doctorInformation.doctorId'
    );
    return forkJoin([
      appends.doctor
        ? this.store.dispatch(new LoadPartnerById(doctorId))
        : of(null),
      appends.partner
        ? this.store.dispatch(new LoadPartnerById(serviceData.partnerId))
        : of(null),
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction(
              { ids: serviceData.followUpIds },
              null,
              null,
              null
            )
          )
        : of(null),
      appends.invoice
        ? this.store
            .dispatch(
              new LoadInvoiceById(
                serviceData.paymentInformation.invoiceId,
                false
              )
            )
            .pipe(
              switchMap(() =>
                this.store.dispatch(
                  new LoadInvoicesPaymentsListAction(
                    serviceData.paymentInformation.invoiceId
                  )
                )
              )
            )
        : of(null),
      appends.samples && serviceData.sampleIds.length
        ? this.store.dispatch(
            new GetSamplesListAction(
              { ids: serviceData.sampleIds },
              {},
              null,
              false
            )
          )
        : of(null),
    ]).pipe(
      switchMap(() =>
        combineLatest([
          of(serviceData),
          this.store
            .select(ProductsListState.getBundles)
            .pipe(map((getBundles) => getBundles(serviceData.bundleIds))),
          appends.partner
            ? this.store
                .select(PartnersState.getPartnerById)
                .pipe(
                  map((getPartnerById) => getPartnerById(serviceData.partnerId))
                )
            : of(null),
          appends.doctor
            ? this.store
                .select(PartnersState.getPartnerById)
                .pipe(map((getPartnerById) => getPartnerById(doctorId)))
            : of(null),
          appends.invoice
            ? combineLatest([
                this.store
                  .select(InvoicesState.getInvoiceById)
                  .pipe(
                    map((getInvoiceById) =>
                      getInvoiceById(serviceData.paymentInformation.invoiceId)
                    )
                  ),
                this.store
                  .select(InvoicesState.getInvoicePymentsByInvoiceId)
                  .pipe(
                    map((getPaymentsByInvoiceId) =>
                      getPaymentsByInvoiceId(
                        serviceData.paymentInformation.invoiceId
                      )
                    )
                  ),
              ]).pipe(map(([invoice, payments]) => ({ ...invoice, payments })))
            : of(null),
          appends.samples
            ? this.store
                .select(SampleState.getSamplesByIds)
                .pipe(
                  map((getSamplesByIds) =>
                    getSamplesByIds(serviceData.sampleIds)
                  )
                )
            : of(null),

          this.store
            .select(FollowUpsState.getFollowUpsByIds)
            .pipe(
              map((getFollowUpsByIds) =>
                getFollowUpsByIds(serviceData?.followUpIds)
              )
            ),
        ])
      ),
      map(
        ([
          serviceData,
          bundles,
          partner,
          doctor,
          invoice,
          samples,
          followUps,
        ]) => ({
          ...serviceData,
          bundles,
          partner,
          doctor,
          invoice,
          samples,
          followUps,
        })
      )
    );
  }

  getExtendedServiceDataList(
    serviceDataList: ServiceData[],
    appends: ServiceDataExtensions
  ): Observable<ExtendedServiceData[]> {
    if (!serviceDataList.length) {
      return of([]);
    }

    return this.loadServiceDataConnectedData(serviceDataList, appends).pipe(
      switchMap(() =>
        combineLatest(
          serviceDataList.map((serviceData) =>
            this.getExtendedServiceData(serviceData, appends)
          )
        )
      )
    );
  }

  loadServiceDataConnectedData(
    serviceDataList: ServiceData[],
    appends: ServiceDataExtensions
  ) {
    if (!serviceDataList.length) {
      return of([]);
    }

    const invoiceIds: string[] = [];
    const partnerIds: string[] = [];
    let followUpIds: string[] = [];
    serviceDataList.filter(Boolean).forEach((serviceData) => {
      let doctorId = this.getPropertyFromServiceInformation(
        serviceData.serviceInformation,
        'doctorInformation.doctorId'
      );

      if (appends.partner) {
        partnerIds.push(serviceData.partnerId);
      }
      if (appends.doctor) {
        partnerIds.push(doctorId);
      }
      if (appends.followUps) {
        followUpIds = [...followUpIds, ...(serviceData.followUpIds ?? [])];
      }
      invoiceIds.push(serviceData.paymentInformation?.invoiceId);
    });

    return forkJoin([
      appends.partner || appends.doctor
        ? this.store.dispatch(new LoadPartnersByIds(partnerIds))
        : of(null),
      appends.invoice
        ? this.store.dispatch(new LoadInvoicesByIdsAction(invoiceIds))
        : of(null),
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction({ ids: followUpIds }, {}, null, false)
          )
        : of(null),
    ]);
  }
  isUnpaid(serviceData: ExtendedServiceData) {
    return (
      serviceData &&
      serviceData.paymentInformation.paymentType !== PaymentType.Partner &&
      !(
        serviceData.invoice.paid ||
        (serviceData.invoice.payments?.[0]?.paid &&
          serviceData.invoice?.properties?.find(
            (property) => property.itemKey === 'SepaContract'
          )?.itemValue === 'true')
      )
    );
  }

  public openDetails(serviceDataId: string) {
    const modalRef = this.ngbModal.open(ServiceDataDetailsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-xxl',
    });

    modalRef.componentInstance.serviceDataId = serviceDataId;
  }

  public openEdit(serviceDataId: string) {
    const modalRef = this.ngbModal.open(ServiceDataFormComponent, {
      centered: true,
      modalDialogClass: 'modal-size-lg',
    });

    modalRef.componentInstance.id = serviceDataId;
  }

  public openResults(serviceDataIds: string[]) {
    const modalRef = this.ngbModal.open(ServiceDataResultsComponent, {
      centered: true,
      modalDialogClass: 'modal-size-md',
    });

    modalRef.componentInstance.serviceDataIds = serviceDataIds;
  }

  public exportSelected(
    exportType: ExportType,
    ids?: string[]
  ): Observable<string> {
    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translate.instant(
        'serviceData.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      )
    );

    return this.exportersUtilsService
      .exportSelected(ExportDialogDataType.ServiceData, ids, exportType)
      .pipe(
        tap((status: string) => {
          if (status !== RuntimeStatus.Running) {
            progressSubject.next({
              value: 100,
              status: 'shared.progress.done',
              success: status === RuntimeStatus.Completed ? true : false,
            });
          }
        })
      );
  }

  public export(
    exportType: ExportType,
    filters?: { [key: string]: string }
  ): Observable<string> {
    const progressSubject = this.exportersUtilsService.openProgressDialog(
      this.translate.instant(
        'serviceData.export.types.' +
          Object.keys(ExportType)[Object.values(ExportType).indexOf(exportType)]
      )
    );

    return this.exportersUtilsService.export(filters, exportType).pipe(
      tap((status: string) => {
        if (status !== RuntimeStatus.Running) {
          progressSubject.next({
            value: 100,
            status: 'shared.progress.done',
            success: status === RuntimeStatus.Completed ? true : false,
          });
        }
      })
    );
  }

  public formToServiceData(
    formData: any,
    inputTypes: DataInputType[]
  ): ProvisionServiceData {
    // Bone marrow transplant is sent only for Galleri and NEG Single Carrier
    const { boneMarrowTransplant, ...sharedPatientHistory } =
      formData.serviceInformation?.shared?.patientHistory ?? {};
    const niptPatientHistory =
      formData.serviceInformation?.nipt?.patientHistory;
    const monoPatientHistory =
      formData.serviceInformation?.mono?.patientHistory;
    const doctorInformation = {
      ...formData.serviceInformation?.shared?.doctorInformation,
      doctorConsent: true,
    };
    const patientGender = formData.serviceInformation?.shared?.patientGender;
    const sharedCurrentPregnancy =
      formData.serviceInformation?.shared?.currentPregnancyInformation;
    const sharedPatientInformation = {
      ...formData.serviceInformation?.shared?.patientInformation,
    };
    const ethnicity = sharedPatientInformation.ethnicity;
    const formAddress = sharedPatientInformation.address;
    const address =
      formAddress?.street || formAddress?.city || formAddress?.zip
        ? formAddress
        : null;
    delete sharedPatientInformation.ethnicity;
    delete sharedPatientInformation.ethnicitySelect;
    delete sharedPatientInformation.address;

    if (sharedPatientHistory?.carrierOfGeneticDiseases?.hasHistory === null) {
      sharedPatientHistory.carrierOfGeneticDiseases = null;
    }

    if (
      sharedPatientHistory?.chromosomalAbnormalityCarrier?.isCarrier === null
    ) {
      sharedPatientHistory.chromosomalAbnormalityCarrier = null;
    }

    if (monoPatientHistory?.monogenicDisorderCarrier?.isCarrier === null) {
      monoPatientHistory.monogenicDisorderCarrier = null;
    }

    const serviceData: ProvisionServiceData = {
      id: formData.id,
      partnerId: formData.partnerId,
      bundleIds: formData.bundleIds,
      onlineReports: formData.onlineReports,
      reportLanguageId: formData.reportLanguageId,
      notes: formData.notes,
      paymentInformation: {
        paymentType: formData.paymentInformation?.paymentType,
        invoiceId: formData.paymentInformation?.invoiceId,
        paid: formData.paymentInformation?.paid,
        geneplanetInvoiceNumber:
          formData.paymentInformation?.geneplanetInvoiceNumber,
        doctorInvoiceNumber: formData.paymentInformation?.doctorInvoiceNumber,
        laboratoryInvoiceNumber:
          formData.paymentInformation?.laboratoryInvoiceNumber,
        notes: formData.paymentInformation?.notes,
      },
      patientInformation: {
        ...sharedPatientInformation,
        firstName: formData.patientInformation?.firstName.trim(),
        lastName: formData.patientInformation?.lastName.trim(),
        dateOfBirth: this.dateOnlyToUtcIsoString(
          formData.patientInformation?.dateOfBirth
        ) as unknown as Date,
        email: formData.patientInformation?.email,
        phoneNumber: formData.patientInformation?.phoneNumber,
        address,
      },
      serviceInformation: {},
    };

    if (inputTypes.includes(DataInputType.NIPT)) {
      serviceData.serviceInformation.nipt = {
        ...formData.serviceInformation?.nipt,
        currentPregnancyInformation: {
          ...formData.serviceInformation?.nipt?.currentPregnancyInformation,
          ...formData.serviceInformation?.shared?.currentPregnancyInformation,
        },
        patientHistory: {
          ...niptPatientHistory,
          ...sharedPatientHistory,
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.NIPPT)) {
      const nippt = formData.serviceInformation?.nippt;
      serviceData.serviceInformation.nippt = {
        ...nippt,
        ethnicity,
        allegedFather: {
          ...this.withoutEthnicitySelect(nippt?.allegedFather),
          firstName: nippt?.allegedFather?.firstName?.trim(),
          lastName: nippt?.allegedFather?.lastName?.trim(),
          dateOfBirth: this.dateOnlyToUtcIsoString(
            nippt?.allegedFather?.dateOfBirth
          ),
        },
        currentPregnancyInformation: {
          expectedConfinementDate:
            sharedCurrentPregnancy?.expectedConfinementDate,
          gestationAge: sharedCurrentPregnancy?.gestationAge,
          isGestationAgeAutomaticallyCalculated:
            sharedCurrentPregnancy?.isGestationAgeAutomaticallyCalculated,
          pregnancyType: sharedCurrentPregnancy?.pregnancyType,
          chorionicityType: sharedCurrentPregnancy?.chorionicityType,
          inVitro: sharedCurrentPregnancy?.inVitro,
        },
        patientHistory: {
          ...nippt?.patientHistory,
          anotherPregnancy: {
            ...nippt?.patientHistory?.anotherPregnancy,
            date: this.dateOnlyToUtcIsoString(
              nippt?.patientHistory?.anotherPregnancy?.date
            ),
          },
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.PATERNITY)) {
      // `patientParticipantId` is form only, the patient's id goes with the patient participant
      const { patientParticipantId, ...paternity } =
        formData.serviceInformation?.paternity ?? {};
      // The patient is the first participant, but is not in the form array
      const patientParticipant: PaternityParticipant = {
        id: patientParticipantId || Util.CreateGuid(),
        firstName: formData.patientInformation?.firstName?.trim(),
        lastName: formData.patientInformation?.lastName?.trim(),
        gender: patientGender,
        dateOfBirth: this.dateOnlyToUtcIsoString(
          formData.patientInformation?.dateOfBirth
        ),
        email: formData.patientInformation?.email,
        relationship: paternity?.relationship,
      };

      serviceData.serviceInformation.paternity = {
        ...paternity,
        additionalParticipants: [
          patientParticipant,
          ...(paternity?.additionalParticipants ?? []).map(
            // Form participant: `dateOfBirth` is a moment
            (participant) => ({
              ...participant,
              id: participant.id || Util.CreateGuid(),
              firstName: participant.firstName?.trim(),
              lastName: participant.lastName?.trim(),
              dateOfBirth: this.dateOnlyToUtcIsoString(participant.dateOfBirth),
            })
          ),
        ],
        patientHistory: {
          ...paternity?.patientHistory,
          isSameBiologicalMother: this.sameBiologicalMotherToApi(paternity),
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.RH)) {
      serviceData.serviceInformation.rhd = {
        ...formData.serviceInformation?.rhd,
        currentPregnancyInformation: {
          ...formData.serviceInformation?.rhd?.currentPregnancyInformation,
          ...formData.serviceInformation?.shared?.currentPregnancyInformation,
        },
        patientHistory: {
          ...formData.serviceInformation?.rhd?.patientHistory,
          ...sharedPatientHistory,
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.CARRIER)) {
      serviceData.serviceInformation.carrier = {
        ...formData.serviceInformation?.carrier,
        patientHistory: {
          ...formData.serviceInformation?.carrier?.patientHistory,
          ...sharedPatientHistory,
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.MONO)) {
      serviceData.serviceInformation.mono = {
        ...formData.serviceInformation?.mono,
        currentPregnancyInformation: {
          ...formData.serviceInformation?.mono?.currentPregnancyInformation,
          ...formData.serviceInformation?.shared?.currentPregnancyInformation,
        },
        patientHistory: {
          ...monoPatientHistory,
          ...sharedPatientHistory,
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.CANCERSCREEN)) {
      serviceData.serviceInformation.cancerScreen = {
        ...formData.serviceInformation?.cancerScreen,
        patientGender,
        patientHistory: {
          ...formData.serviceInformation?.cancerScreen?.patientHistory,
          ...sharedPatientHistory,
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.GALLERI)) {
      serviceData.serviceInformation.galleri = {
        ...formData.serviceInformation?.galleri,
        patientHistory: {
          ...formData.serviceInformation?.galleri?.patientHistory,
          boneMarrowTransplant,
        },
        patientGender,
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (inputTypes.includes(DataInputType.NEGSINGLECARRIER)) {
      serviceData.serviceInformation.negSingleCarrier = {
        patientGender,
        ethnicity,
        patientHistory: {
          boneMarrowTransplant,
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    if (
      inputTypes.includes(DataInputType.VENISAFE) ||
      inputTypes.includes(DataInputType.VENISAFEMATERNA)
    ) {
      serviceData.serviceInformation.venisafe = {
        ...formData.serviceInformation?.venisafe,
        patientHistory: {
          ...formData.serviceInformation?.venisafe?.patientHistory,
          ...sharedPatientHistory,
        },
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };

      if (inputTypes.includes(DataInputType.VENISAFE)) {
        serviceData.serviceInformation.venisafe.ethnicity = ethnicity;
      }
    }

    if (inputTypes.includes(DataInputType.OTHER)) {
      serviceData.serviceInformation.other = {
        ...formData.serviceInformation?.other,
        doctorInformation,
        comments: formData.serviceInformation?.shared?.comments,
      };
    }

    return serviceData;
  }

  // `ethnicitySelect` is a form-only helper for the ethnicity select
  private withoutEthnicitySelect(group: { ethnicitySelect?: unknown }) {
    const values = { ...group };
    delete values.ethnicitySelect;

    return values;
  }

  // Only asked for Fraternity; "Not available" is sent as null
  private sameBiologicalMotherToApi(paternity: {
    parentingTestType: ParentingTestType;
    patientHistory: { isSameBiologicalMother: SameBiologicalMotherAnswer };
  }): boolean | null {
    if (paternity?.parentingTestType !== ParentingTestType.Fraternity) {
      return null;
    }
    const answer = paternity.patientHistory?.isSameBiologicalMother;
    if (answer === SameBiologicalMotherAnswer.Yes) {
      return true;
    }
    if (answer === SameBiologicalMotherAnswer.No) {
      return false;
    }
    return null;
  }

  // null means "Not available" only when the question was asked (Fraternity)
  private sameBiologicalMotherToForm(
    paternity: PaternityServiceInformation
  ): SameBiologicalMotherAnswer | null {
    if (paternity?.parentingTestType !== ParentingTestType.Fraternity) {
      return null;
    }
    const answer = paternity.patientHistory?.isSameBiologicalMother;
    if (answer === true) {
      return SameBiologicalMotherAnswer.Yes;
    }
    if (answer === false) {
      return SameBiologicalMotherAnswer.No;
    }
    return SameBiologicalMotherAnswer.NotAvailable;
  }

  // Ethnicity is a select, "Other" ethnicities are stored as free text
  private ethnicityToForm(ethnicity: string) {
    if (!ethnicity) {
      return { ethnicity: null, ethnicitySelect: null };
    }

    return {
      ethnicity,
      ethnicitySelect: Object.values<string>(Ethnicity).includes(ethnicity)
        ? ethnicity
        : Ethnicity.Other,
    };
  }

  private dateOnlyToUtcIsoString(date: moment.Moment): string {
    if (!date) {
      return null;
    }

    return getSafeUTCDate(date).startOf('day').toISOString();
  }

  serviceDataToForm(serviceData: ServiceData, inputTypes: DataInputType[]) {
    const formData = {
      ...serviceData,
      patientInformation: {
        ...serviceData.patientInformation,
        dateOfBirth: getDateOnlyMoment(
          serviceData.patientInformation.dateOfBirth
        ),
      },
      serviceInformation: {
        shared: {
          patientGender: null,
          currentPregnancyInformation: {},
          patientHistory: {},
          doctorInformation: {},
          patientInformation: { ...serviceData.patientInformation },
        },
      },
    } as any;

    if (inputTypes.includes(DataInputType.NIPT)) {
      formData.serviceInformation.nipt = {
        ...serviceData.serviceInformation.nipt,
        comments: serviceData.serviceInformation.nipt?.comments,
      };
      formData.serviceInformation.shared.currentPregnancyInformation = {
        ...formData.serviceInformation.shared.currentPregnancyInformation,
        ...serviceData.serviceInformation.nipt?.currentPregnancyInformation,
      };
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        ...serviceData.serviceInformation.nipt?.patientHistory,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.nipt?.doctorInformation,
      };
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.nipt?.comments;
    }

    if (inputTypes.includes(DataInputType.NIPPT)) {
      const nippt = serviceData.serviceInformation.nippt;
      formData.serviceInformation.nippt = {
        ...nippt,
        allegedFather: {
          ...nippt?.allegedFather,
          ...this.ethnicityToForm(nippt?.allegedFather?.ethnicity),
          dateOfBirth: getDateOnlyMoment(nippt?.allegedFather?.dateOfBirth),
        },
        patientHistory: {
          ...nippt?.patientHistory,
          anotherPregnancy: {
            ...nippt?.patientHistory?.anotherPregnancy,
            date: getDateOnlyMoment(
              nippt?.patientHistory?.anotherPregnancy?.date
            ),
          },
        },
      };
      formData.serviceInformation.shared.currentPregnancyInformation = {
        ...formData.serviceInformation.shared.currentPregnancyInformation,
        ...nippt?.currentPregnancyInformation,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...nippt?.doctorInformation,
      };
      formData.serviceInformation.shared.comments = nippt?.comments;
      formData.serviceInformation.shared.patientInformation = {
        ...formData.serviceInformation.shared.patientInformation,
        ...this.ethnicityToForm(nippt?.ethnicity),
      };
    }

    if (inputTypes.includes(DataInputType.PATERNITY)) {
      const paternity = serviceData.serviceInformation.paternity;
      // The patient is the first participant, the rest are the form array
      const [patientParticipant, ...additionalParticipants] =
        paternity?.additionalParticipants ?? [];
      formData.serviceInformation.paternity = {
        ...paternity,
        patientParticipantId: patientParticipant?.id ?? null,
        relationship: patientParticipant?.relationship ?? null,
        additionalParticipants: additionalParticipants.map((participant) => ({
          ...participant,
          dateOfBirth: getDateOnlyMoment(participant.dateOfBirth),
        })),
        patientHistory: {
          ...paternity?.patientHistory,
          isSameBiologicalMother: this.sameBiologicalMotherToForm(paternity),
        },
      };
      formData.serviceInformation.shared.patientGender ??=
        patientParticipant?.gender;
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...paternity?.doctorInformation,
      };
      formData.serviceInformation.shared.comments = paternity?.comments;
    }

    if (inputTypes.includes(DataInputType.RH)) {
      formData.serviceInformation.rhd = {
        ...serviceData.serviceInformation.rhd,
      };
      formData.serviceInformation.shared.currentPregnancyInformation = {
        ...formData.serviceInformation.shared.currentPregnancyInformation,
        ...serviceData.serviceInformation.rhd?.currentPregnancyInformation,
      };
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        ...serviceData.serviceInformation.rhd?.patientHistory,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.rhd?.doctorInformation,
      };
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.rhd?.comments;
    }

    if (inputTypes.includes(DataInputType.CARRIER)) {
      formData.serviceInformation.carrier = {
        ...serviceData.serviceInformation.carrier,
      };
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        ...serviceData.serviceInformation.carrier?.patientHistory,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.carrier?.doctorInformation,
      };
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.carrier?.comments;
    }

    if (inputTypes.includes(DataInputType.MONO)) {
      formData.serviceInformation.mono = {
        ...serviceData.serviceInformation.mono,
      };
      formData.serviceInformation.shared.currentPregnancyInformation = {
        ...formData.serviceInformation.shared.currentPregnancyInformation,
        ...serviceData.serviceInformation.mono?.currentPregnancyInformation,
      };
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        ...serviceData.serviceInformation.mono?.patientHistory,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.mono?.doctorInformation,
      };
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.mono?.comments;
    }

    if (inputTypes.includes(DataInputType.CANCERSCREEN)) {
      formData.serviceInformation.cancerScreen = {
        ...serviceData.serviceInformation.cancerScreen,
      };
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        ...serviceData.serviceInformation.cancerScreen?.patientHistory,
      };
      formData.serviceInformation.shared.patientGender ??=
        serviceData.serviceInformation.cancerScreen?.patientGender;
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.cancerScreen?.doctorInformation,
      };
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.cancerScreen?.comments;
    }

    if (inputTypes.includes(DataInputType.GALLERI)) {
      formData.serviceInformation.galleri = {
        ...serviceData.serviceInformation.galleri,
      };
      formData.serviceInformation.shared.patientGender ??=
        serviceData.serviceInformation.galleri?.patientGender;
      // The bone marrow transplant answer is asked as the shared question
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        boneMarrowTransplant:
          serviceData.serviceInformation.galleri?.patientHistory
            ?.boneMarrowTransplant,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.galleri?.doctorInformation,
      };
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.galleri?.comments;
    }

    if (inputTypes.includes(DataInputType.NEGSINGLECARRIER)) {
      const negSingleCarrier = serviceData.serviceInformation.negSingleCarrier;
      formData.serviceInformation.negSingleCarrier = { ...negSingleCarrier };
      formData.serviceInformation.shared.patientGender ??=
        negSingleCarrier?.patientGender;
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        boneMarrowTransplant:
          negSingleCarrier?.patientHistory?.boneMarrowTransplant ??
          formData.serviceInformation.shared.patientHistory
            .boneMarrowTransplant,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...negSingleCarrier?.doctorInformation,
      };
      formData.serviceInformation.shared.comments = negSingleCarrier?.comments;
      formData.serviceInformation.shared.patientInformation = {
        ...formData.serviceInformation.shared.patientInformation,
        ...this.ethnicityToForm(negSingleCarrier?.ethnicity),
      };
    }

    if (
      inputTypes.includes(DataInputType.VENISAFE) ||
      inputTypes.includes(DataInputType.VENISAFEMATERNA)
    ) {
      formData.serviceInformation.venisafe = {
        ...serviceData.serviceInformation.venisafe,
      };
      formData.serviceInformation.shared.patientHistory = {
        ...formData.serviceInformation.shared.patientHistory,
        ...serviceData.serviceInformation.venisafe?.patientHistory,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.venisafe?.doctorInformation,
      };
      if (inputTypes.includes(DataInputType.VENISAFE)) {
        const ethnicity = serviceData.serviceInformation.venisafe?.ethnicity;
        formData.serviceInformation.shared.patientInformation = {
          ...formData.serviceInformation.shared.patientInformation,
          ethnicity,
          ethnicitySelect: Object.keys(Ethnicity).includes(ethnicity)
            ? ethnicity
            : Ethnicity.Other,
        };
      }
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.venisafe?.comments;
    }
    if (inputTypes.includes(DataInputType.OTHER)) {
      formData.serviceInformation.other = {
        ...serviceData.serviceInformation.other,
      };
      formData.serviceInformation.shared.doctorInformation = {
        ...formData.serviceInformation.shared.doctorInformation,
        ...serviceData.serviceInformation.other?.doctorInformation,
      };
      formData.serviceInformation.shared.comments =
        serviceData.serviceInformation.other?.comments;
    }

    formData.serviceInformation.shared.currentPregnancyInformation.ultrasoundDate =
      formData.serviceInformation?.shared?.currentPregnancyInformation
        ?.ultrasoundDate
        ? moment(
            formData.serviceInformation?.shared?.currentPregnancyInformation
              ?.ultrasoundDate
          )
        : null;

    formData.serviceInformation.shared.currentPregnancyInformation.expectedConfinementDate =
      formData?.serviceInformation?.shared?.currentPregnancyInformation
        ?.expectedConfinementDate
        ? moment(
            formData.serviceInformation.shared.currentPregnancyInformation
              .expectedConfinementDate
          )
        : null;

    return formData;
  }

  public getPropertyFromServiceInformation(
    serviceInformation: ServiceInformation | SampleServiceInformation,
    property: string
  ) {
    if (!serviceInformation) {
      return undefined;
    }
    return get(
      serviceInformation[
        Object.keys(serviceInformation).find((key) =>
          get(serviceInformation[key], property)
        )
      ],
      property
    );
  }

  public openServiceDataByKitId(kitId: string) {
    this.serviceDataService
      .getServiceDataList(
        {
          kitIds: [kitId],
        },
        {}
      )
      .pipe(
        switchMap((result) =>
          this.store
            .dispatch(new SetServiceDataAction(result.items))
            .pipe(map(() => result))
        )
      )
      .subscribe({
        next: (result) => {
          const item = result.items?.[0];
          if (item) {
            this.openDetails(item.id);
          }
        },
        error: () => {
          this.toastService.danger(
            `Service data not found for this Kit ID`,
            'Not found!',
            5000
          );
        },
      });
  }
}
